import { useCallback, useEffect, useMemo, useState } from "react";
import {
  AlertTriangle,
  CheckCircle2,
  Database,
  DatabaseBackup,
  Eye,
  EyeOff,
  Info,
  Link2,
  Loader2,
  ShieldAlert,
  Sparkles,
  X,
} from "lucide-react";
import { api, errorToMessage } from "@/lib/apiClient";
import { useAuth } from "@/lib/auth";
import { SYSTEM_MAINTENANCE as T } from "@/constants/testIds";

const CONFIRM_PHRASE = "RESET DATA";

function bytes(n) {
  const v = Number(n || 0);
  if (v < 1024) return `${v} B`;
  if (v < 1024 * 1024) return `${(v / 1024).toFixed(1)} KB`;
  if (v < 1024 * 1024 * 1024) return `${(v / (1024 * 1024)).toFixed(2)} MB`;
  return `${(v / (1024 * 1024 * 1024)).toFixed(2)} GB`;
}

function Modal({ title, open, onClose, children, wide = false }) {
  if (!open) return null;
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/50 p-4">
      <div
        className={`w-full ${wide ? "max-w-xl" : "max-w-lg"} rounded-lg bg-white shadow-xl`}
      >
        <div className="flex items-center justify-between border-b border-slate-100 px-5 py-3">
          <h3 className="text-sm font-semibold text-slate-900">{title}</h3>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-700">
            <X size={16} />
          </button>
        </div>
        <div className="px-5 py-4">{children}</div>
      </div>
    </div>
  );
}

export default function SystemMaintenancePage() {
  const { user } = useAuth();

  // ---- Module registry -----------------------------------------------------
  const [modules, setModules] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // ---- Selection state -----------------------------------------------------
  // `userSelected` = modules the operator explicitly ticked.
  // `plan`         = server-computed closure (all_modules ⊇ userSelected).
  // `autoSelected` = derived: all_modules − userSelected.
  const [userSelected, setUserSelected] = useState(() => new Set());
  const [plan, setPlan] = useState(null);
  const [planBusy, setPlanBusy] = useState(false);
  // Transient hint shown when the user clicks a disabled required-dep row.
  const [depHint, setDepHint] = useState(null); // { moduleKey, timer }

  // ---- Confirm modal state -------------------------------------------------
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [confirmText, setConfirmText] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [confirmReason, setConfirmReason] = useState("");
  const [executing, setExecuting] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [result, setResult] = useState(null);
  const [actionErr, setActionErr] = useState(null);

  // ---- Load module registry ------------------------------------------------
  useEffect(() => {
    let ok = true;
    (async () => {
      setLoading(true);
      setError(null);
      try {
        const { data } = await api.get("/system-maintenance/modules");
        if (ok) setModules(data);
      } catch (e) {
        if (ok) setError(errorToMessage(e, "Failed to load modules"));
      } finally {
        if (ok) setLoading(false);
      }
    })();
    return () => {
      ok = false;
    };
  }, []);

  // ---- Derived selection sets ---------------------------------------------
  const allSelected = useMemo(() => {
    if (plan?.all_modules?.length) return new Set(plan.all_modules);
    return new Set(userSelected);
  }, [plan, userSelected]);

  const autoSelected = useMemo(() => {
    if (!plan?.all_modules) return new Set();
    const s = new Set();
    for (const k of plan.all_modules) {
      if (!userSelected.has(k)) s.add(k);
    }
    return s;
  }, [plan, userSelected]);

  const selectedCount = allSelected.size;

  const labelFor = useCallback(
    (key) => modules.find((m) => m.key === key)?.label || key,
    [modules],
  );

  // ---- Backend calls -------------------------------------------------------
  const refreshPlan = useCallback(async (keys) => {
    const list = Array.from(keys);
    if (list.length === 0) {
      setPlan(null);
      return;
    }
    setPlanBusy(true);
    setActionErr(null);
    try {
      const { data } = await api.post("/system-maintenance/plan", {
        modules: list,
      });
      setPlan(data);
    } catch (e) {
      setActionErr(errorToMessage(e, "Failed to compute dependencies"));
    } finally {
      setPlanBusy(false);
    }
  }, []);

  // ---- Toggle handler ------------------------------------------------------
  // Rules:
  //   - Toggling ON a module: add it to userSelected + refresh plan.
  //   - Toggling OFF a userSelected module: remove + refresh plan (deps update).
  //   - Toggling OFF an auto-selected module: refuse; show inline hint.
  const toggle = useCallback(
    (key) => {
      if (autoSelected.has(key)) {
        // Required dependency — cannot be un-ticked directly.
        setDepHint({ moduleKey: key });
        setTimeout(() => setDepHint(null), 3200);
        return;
      }
      const next = new Set(userSelected);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      setUserSelected(next);
      setResult(null);
      refreshPlan(next);
    },
    [autoSelected, userSelected, refreshPlan],
  );

  const selectAll = () => {
    const all = new Set(modules.map((m) => m.key));
    setUserSelected(all);
    setResult(null);
    refreshPlan(all);
  };
  const clearAll = () => {
    setUserSelected(new Set());
    setPlan(null);
    setResult(null);
  };
  const factoryReset = () => {
    selectAll();
  };

  // ---- Confirm modal -------------------------------------------------------
  const openConfirm = () => {
    if (selectedCount === 0) return;
    setConfirmText("");
    setConfirmPassword("");
    setConfirmReason("");
    setShowPassword(false);
    setActionErr(null);
    setResult(null);
    setConfirmOpen(true);
  };

  const executeReset = async () => {
    if (!plan) return;
    setExecuting(true);
    setActionErr(null);
    try {
      const { data } = await api.post("/system-maintenance/reset", {
        modules: plan.all_modules,
        password: confirmPassword,
        confirmation: confirmText,
        reason: confirmReason,
      });
      setResult(data);
      setUserSelected(new Set());
      setPlan(null);
      setConfirmOpen(false);
    } catch (e) {
      // The auto-selection guarantees FK closure, so dependencies_required /
      // fk_violation should be impossible here. If one somehow slips through
      // (server-side race?), silently re-plan and let the user retry — the
      // operator must NEVER see FK-jargon in Smart Reset (spec #7).
      const detail = e?.response?.data?.detail;
      if (
        detail?.error === "dependencies_required" ||
        detail?.error === "fk_violation"
      ) {
        const nextAll = detail.all_modules || detail.dependencies?.all_modules;
        if (nextAll?.length) {
          // Compare against the plan the user has just confirmed. If the
          // server did NOT actually expand the module set (same closure as
          // before), showing "Dependency set has updated — review and retry"
          // is misleading and will loop forever (see e.g. an unresolvable
          // orphan_tables entry). In that case surface a concrete error
          // instead of asking the operator to re-review an unchanged plan.
          const prevAll = Array.isArray(plan?.all_modules) ? plan.all_modules : [];
          const same =
            nextAll.length === prevAll.length &&
            new Set(nextAll).size === new Set([...nextAll, ...prevAll]).size;
          if (same) {
            const orphans =
              detail.orphan_tables ||
              detail.dependencies?.orphan_tables ||
              [];
            if (orphans.length) {
              const list = orphans
                .map((o) => (o && o.table) ? o.table : String(o))
                .join(", ");
              setActionErr(
                `Reset blocked by hidden foreign-key dependency on table(s): ${list}. ` +
                  "These tables are not part of any reset module and must be " +
                  "cleared manually before Smart Reset can proceed.",
              );
            } else {
              setActionErr(
                errorToMessage(e, "Reset failed") ||
                  (detail.message ?? "Reset failed"),
              );
            }
            setExecuting(false);
            return;
          }
          // Genuine expansion — server discovered additional required
          // modules. Update selection, re-plan and ask the user to review.
          setUserSelected(new Set(nextAll));
          await refreshPlan(new Set(nextAll));
          setActionErr(
            "Dependency set has updated. Please review the module list above and retry.",
          );
          setExecuting(false);
          return;
        }
      }
      setActionErr(errorToMessage(e, "Reset failed"));
    } finally {
      setExecuting(false);
    }
  };

  const canExecute =
    confirmText.trim() === CONFIRM_PHRASE &&
    confirmPassword.length > 0 &&
    confirmReason.trim().length >= 3 &&
    !executing;

  // ---- Render helpers ------------------------------------------------------
  const renderModuleCard = (m) => {
    const isUser = userSelected.has(m.key);
    const isAuto = autoSelected.has(m.key);
    const isChecked = isUser || isAuto;
    const disabled = isAuto;
    const hinted = depHint?.moduleKey === m.key;
    return (
      <label
        key={m.key}
        onClick={disabled ? (e) => { e.preventDefault(); toggle(m.key); } : undefined}
        className={`relative flex items-start gap-2 rounded-md border px-3 py-2 transition min-h-[68px] ${
          disabled ? "cursor-not-allowed" : "cursor-pointer"
        } ${
          isAuto
            ? "border-emerald-300 bg-emerald-50/60"
            : isUser
              ? "border-slate-900 bg-slate-50"
              : "border-slate-200 hover:border-slate-300"
        } ${hinted ? "ring-2 ring-amber-400" : ""}`}
      >
        <input
          type="checkbox"
          checked={isChecked}
          disabled={disabled}
          onChange={() => toggle(m.key)}
          data-testid={T.moduleCheckbox(m.key)}
          className="mt-0.5"
        />
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <div className="text-sm font-medium text-slate-900">{m.label}</div>
            {isAuto && (
              <span
                className="inline-flex items-center gap-1 rounded-full bg-emerald-100 text-emerald-800 text-[10px] px-2 py-0.5"
                data-testid={T.requiredBadge(m.key)}
              >
                <Link2 size={10} /> Required dependency
              </span>
            )}
          </div>
          <div className="text-[11px] text-slate-500 font-mono break-words">
            {m.tables.length > 0
              ? m.tables.join(", ")
              : "battery fields on laptop_passport"}
          </div>
          {hinted && (
            <div
              className="mt-1 text-[11px] text-amber-800"
              data-testid={T.requiredHint}
            >
              This module is required by another selection. Deselect the parent
              module to remove it.
            </div>
          )}
        </div>
      </label>
    );
  };

  // ---- Render --------------------------------------------------------------
  return (
    <div className="space-y-6" data-testid={T.page}>
      <div className="flex items-start justify-between gap-4">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight text-slate-900">
            System Maintenance
          </h1>
          <p className="text-sm text-slate-500 mt-1">
            Destructive housekeeping tools scoped to your company. SUPER_ADMIN
            only. Dependencies are resolved automatically — pick any module and
            the system will include everything else that must be wiped with it.
          </p>
        </div>
      </div>

      <div className="rounded-md border border-emerald-200 bg-emerald-50 px-4 py-3 text-sm text-emerald-900">
        <div className="flex items-start gap-2">
          <Sparkles size={16} className="mt-0.5" />
          <div>
            <div className="font-semibold">Smart Reset</div>
            <div className="text-xs mt-1">
              Every time you tick or untick a module, the foreign-key graph is
              walked and dependent modules are auto-added. Auto-added modules
              show a{" "}
              <span className="rounded-full bg-emerald-100 text-emerald-800 text-[10px] px-1.5 py-0.5">
                Required dependency
              </span>{" "}
              badge and can only be removed by unticking the parent that pulled
              them in.
            </div>
          </div>
        </div>
      </div>

      {error && (
        <div
          className="rounded-md border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-700"
          data-testid={T.errorBanner}
        >
          {error}
        </div>
      )}
      {actionErr && (
        <div
          className="rounded-md border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-700"
          data-testid={T.errorBanner}
        >
          {actionErr}
        </div>
      )}
      {result && (
        <div
          className="rounded-md border border-emerald-200 bg-emerald-50 px-4 py-3 text-sm text-emerald-900"
          data-testid={T.successBanner}
        >
          <div className="flex items-start gap-2">
            <CheckCircle2 size={16} className="mt-0.5" />
            <div className="space-y-1">
              <div className="font-semibold">
                Reset complete — {result.total_deleted} row(s) affected across{" "}
                {Object.keys(result.deleted).length} table(s).
              </div>
              <div className="text-xs">
                Backup: <span className="font-mono">{result.backup.filename}</span> ({bytes(result.backup.size_bytes)})
              </div>
              <details className="text-xs mt-1">
                <summary className="cursor-pointer">Row breakdown & execution order</summary>
                <div className="mt-1 grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <div>
                    <div className="font-semibold mb-1">Deleted rows</div>
                    <ul className="space-y-0.5">
                      {Object.entries(result.deleted).map(([k, v]) => (
                        <li key={k} className="font-mono">{k}: {v}</li>
                      ))}
                    </ul>
                  </div>
                  <div>
                    <div className="font-semibold mb-1">Execution order</div>
                    <ol className="list-decimal list-inside font-mono space-y-0.5">
                      {result.order.map((t) => <li key={t}>{t}</li>)}
                    </ol>
                  </div>
                </div>
              </details>
            </div>
          </div>
        </div>
      )}

      {/* Module picker */}
      <div className="rounded-lg border border-slate-200 bg-white shadow-sm">
        <div className="flex items-center justify-between border-b border-slate-100 px-4 py-3">
          <div className="text-sm font-medium text-slate-800 flex items-center gap-2">
            Modules selected: {selectedCount}/{modules.length}
            {planBusy && <Loader2 size={12} className="animate-spin text-slate-400" />}
            {autoSelected.size > 0 && (
              <span className="text-xs text-emerald-700 font-normal">
                ({autoSelected.size} auto-added)
              </span>
            )}
          </div>
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={selectAll}
              data-testid={T.selectAll}
              className="rounded-md border border-slate-300 bg-white px-3 py-1.5 text-xs text-slate-700 hover:bg-slate-50"
            >
              Select All
            </button>
            <button
              type="button"
              onClick={clearAll}
              data-testid={T.clearAll}
              className="rounded-md border border-slate-300 bg-white px-3 py-1.5 text-xs text-slate-700 hover:bg-slate-50"
            >
              Clear Selection
            </button>
          </div>
        </div>
        <div className="p-4 grid grid-cols-1 sm:grid-cols-2 gap-2">
          {loading && (
            <div className="col-span-2 text-center text-sm text-slate-400 py-6">
              Loading modules…
            </div>
          )}
          {!loading && modules.map(renderModuleCard)}
        </div>
      </div>

      {/* Action bar */}
      <div className="flex items-center gap-2 flex-wrap">
        <button
          type="button"
          onClick={() => refreshPlan(userSelected)}
          disabled={selectedCount === 0 || planBusy}
          data-testid={T.dryRunBtn}
          className="inline-flex items-center gap-2 rounded-md border border-slate-300 bg-white px-4 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50 disabled:opacity-40"
        >
          {planBusy ? <Loader2 size={14} className="animate-spin" /> : <Database size={14} />}
          Refresh Dry Run
        </button>
        <button
          type="button"
          onClick={openConfirm}
          disabled={selectedCount === 0 || !plan}
          data-testid={T.resetBtn}
          className="inline-flex items-center gap-2 rounded-md bg-rose-600 px-4 py-2 text-sm font-medium text-white hover:bg-rose-700 disabled:opacity-40"
        >
          <DatabaseBackup size={14} /> Reset Selected Modules
        </button>
        <div className="ml-auto">
          <button
            type="button"
            onClick={factoryReset}
            data-testid={T.factoryResetBtn}
            className="inline-flex items-center gap-2 rounded-md border border-rose-300 bg-rose-50 px-4 py-2 text-sm font-medium text-rose-700 hover:bg-rose-100"
          >
            <ShieldAlert size={14} /> Factory Reset
          </button>
        </div>
      </div>

      {/* Dry Run table (Module | Records | Total) */}
      {plan && (
        <div
          className="rounded-lg border border-slate-200 bg-white shadow-sm overflow-hidden"
          data-testid={T.dryRunResults}
        >
          <div className="border-b border-slate-100 px-4 py-3 flex items-baseline justify-between">
            <div className="text-sm font-medium text-slate-800">
              Deletion Summary — {plan.dry_run.total_rows} record(s) will be affected
            </div>
            {autoSelected.size > 0 && (
              <div className="text-xs text-emerald-700">
                {autoSelected.size} module(s) auto-included
              </div>
            )}
          </div>
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-xs uppercase tracking-wide text-slate-500">
              <tr>
                <th className="text-left px-4 py-2">Module</th>
                <th className="text-right px-4 py-2">Records</th>
              </tr>
            </thead>
            <tbody>
              {plan.dry_run.modules.map((mod) => (
                <tr key={mod.key} className="border-t border-slate-100">
                  <td className="px-4 py-2 text-slate-800">
                    {mod.label}
                    {autoSelected.has(mod.key) && (
                      <span className="ml-2 rounded-full bg-emerald-100 text-emerald-800 text-[10px] px-1.5 py-0.5">
                        Required
                      </span>
                    )}
                  </td>
                  <td className="px-4 py-2 text-right font-mono text-xs">
                    {mod.total_rows}
                  </td>
                </tr>
              ))}
              <tr className="border-t-2 border-slate-300 bg-slate-50">
                <td className="px-4 py-2 font-semibold">Total Records</td>
                <td
                  className="px-4 py-2 text-right font-mono font-semibold"
                  data-testid={T.dryRunTotal}
                >
                  {plan.dry_run.total_rows}
                </td>
              </tr>
            </tbody>
          </table>
          {plan.orphan_tables && plan.orphan_tables.length > 0 && (
            <div className="border-t border-amber-200 bg-amber-50 px-4 py-3 text-xs text-amber-900">
              <div className="flex items-start gap-2">
                <Info size={12} className="mt-0.5" />
                <div>
                  <div className="font-semibold mb-1">
                    Notes on data outside the reset scope:
                  </div>
                  <ul className="list-disc list-inside space-y-0.5">
                    {plan.orphan_tables.map((o) => (
                      <li key={o.table}>
                        <span className="font-mono">{o.table}</span> — {o.reason}
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Confirmation modal — combines review + RESET DATA + password + reason */}
      <Modal
        title="Confirm Smart Reset"
        open={confirmOpen}
        onClose={() => (executing ? null : setConfirmOpen(false))}
        wide
      >
        <div className="space-y-3" data-testid={T.confirmModal}>
          <div className="rounded-md border border-rose-200 bg-rose-50 p-3 text-sm text-rose-900">
            <div className="flex items-start gap-2">
              <AlertTriangle size={16} className="mt-0.5" />
              <div>
                <div className="font-semibold">This is destructive.</div>
                <div className="text-xs mt-1">
                  An automatic backup will be created first. If the backup
                  fails, the reset is aborted. All modules below are wiped in
                  a single transaction; any failure rolls the entire operation
                  back.
                </div>
              </div>
            </div>
          </div>

          <div>
            <div className="text-xs font-semibold text-slate-700 mb-1">
              Selected Modules ({plan?.all_modules?.length ?? 0})
            </div>
            <ul className="rounded-md border border-slate-200 bg-slate-50 px-4 py-2 text-sm max-h-52 overflow-y-auto">
              {(plan?.all_modules || []).map((k) => (
                <li key={k} className="flex items-center gap-2 py-0.5">
                  <CheckCircle2
                    size={14}
                    className={autoSelected.has(k) ? "text-emerald-600" : "text-slate-500"}
                  />
                  <span>{labelFor(k)}</span>
                  {autoSelected.has(k) && (
                    <span className="ml-1 text-[10px] text-emerald-700">(Required)</span>
                  )}
                </li>
              ))}
            </ul>
            {autoSelected.size > 0 && (
              <div className="text-[11px] text-slate-500 mt-1">
                {autoSelected.size} module(s) were automatically included because
                they depend on your selection.
              </div>
            )}
            {plan?.dry_run && (
              <div className="text-[11px] text-slate-600 mt-1">
                Total records that will be deleted:{" "}
                <span className="font-mono font-semibold">
                  {plan.dry_run.total_rows}
                </span>
              </div>
            )}
            {plan?.append_only_triggers?.length > 0 && (
              <details className="mt-2 text-[11px] text-amber-800 rounded-md border border-amber-200 bg-amber-50 px-2 py-1">
                <summary className="cursor-pointer font-semibold">
                  {plan.append_only_triggers.length} append-only guard(s) will be
                  transactionally bypassed
                </summary>
                <ul className="mt-1 font-mono list-disc list-inside space-y-0.5">
                  {plan.append_only_triggers.map((t) => (
                    <li key={`${t.table}.${t.trigger}`}>
                      {t.table} → {t.trigger}
                    </li>
                  ))}
                </ul>
                <div className="mt-1 not-italic">
                  These triggers are DISABLE-d and re-ENABLE-d inside the same
                  reset transaction. If the reset rolls back, the guards are
                  restored automatically.
                </div>
              </details>
            )}
          </div>

          <label className="block">
            <span className="block text-xs font-medium text-slate-500 mb-1">
              Type <span className="font-mono">{CONFIRM_PHRASE}</span> to confirm
            </span>
            <input
              value={confirmText}
              onChange={(e) => setConfirmText(e.target.value)}
              data-testid={T.confirmText}
              className="w-full rounded-md border border-slate-200 px-3 py-2 text-sm"
              autoComplete="off"
            />
          </label>
          <label className="block">
            <span className="block text-xs font-medium text-slate-500 mb-1">
              Re-enter your password ({user?.username})
            </span>
            <div className="relative">
              <input
                type={showPassword ? "text" : "password"}
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                data-testid={T.confirmPassword}
                className="w-full rounded-md border border-slate-200 pr-10 px-3 py-2 text-sm font-mono"
                autoComplete="off"
                autoCorrect="off"
                spellCheck={false}
                name="sm-reset-password"
              />
              <button
                type="button"
                onClick={() => setShowPassword((v) => !v)}
                data-testid={T.confirmPasswordToggle}
                aria-label={showPassword ? "Hide password" : "Show password"}
                className="absolute inset-y-0 right-2 flex items-center text-slate-400 hover:text-slate-700"
              >
                {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
              </button>
            </div>
            <div className="mt-1 text-[11px] text-slate-500 flex items-center justify-between">
              <span>{confirmPassword.length} character(s) typed</span>
              <span className="text-slate-400">Autofill disabled — please type manually</span>
            </div>
          </label>
          <label className="block">
            <span className="block text-xs font-medium text-slate-500 mb-1">
              Reason (audit log entry)
            </span>
            <textarea
              value={confirmReason}
              onChange={(e) => setConfirmReason(e.target.value)}
              data-testid={T.confirmReason}
              rows={2}
              className="w-full rounded-md border border-slate-200 px-3 py-2 text-sm"
              placeholder="Why is this reset being performed?"
            />
          </label>
          {actionErr && (
            <div className="rounded-md border border-rose-200 bg-rose-50 px-3 py-2 text-xs text-rose-700">
              {actionErr}
            </div>
          )}
          <div className="flex justify-end gap-2">
            <button
              onClick={() => setConfirmOpen(false)}
              disabled={executing}
              data-testid={T.confirmCancel}
              className="rounded-md border border-slate-300 bg-white px-3 py-1.5 text-sm text-slate-700 hover:bg-slate-50 disabled:opacity-40"
            >
              Cancel
            </button>
            <button
              onClick={executeReset}
              disabled={!canExecute}
              data-testid={T.confirmExecute}
              className="inline-flex items-center gap-2 rounded-md bg-rose-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-rose-700 disabled:opacity-40"
            >
              {executing && <Loader2 size={14} className="animate-spin" />}
              {executing ? "Resetting…" : "Backup & Reset now"}
            </button>
          </div>
        </div>
      </Modal>
    </div>
  );
}
